(() => {
  if (window.LeslisSearchAdapter) return;
  let config;
  try { config = JSON.parse(document.getElementById('LeslisSearchConfig')?.textContent || '{}'); } catch { return; }
  const initialized = new WeakSet();
  const grids = new WeakSet();
  let attachedForms = 0;
  let ready = false;
  const query = (value) => String(value || '').trim().replace(/\s+/g, ' ').slice(0, 120);
  const normalize = (value) => query(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
  const searchTypes = (form) => {
    const value = form?.querySelector('[name="type"]')?.value || 'product,article,page';
    const types = [...new Set(value.split(',').map((type) => type.trim()).filter((type) => ['product', 'article', 'page'].includes(type)))];
    return types.length ? types : ['product', 'article', 'page'];
  };
  const errorState = (owner, visible) => { const node = owner.querySelector('[data-leslis-search-error]'); if (node) node.hidden = !visible; };

  function filterIdeas(form) {
    const input = normalize(form.querySelector('input[name="q"]')?.value);
    const group = form.querySelector('[data-leslis-search-ideas]');
    if (!group) return;
    const links = Array.from(group.querySelectorAll('[data-leslis-search-idea]'));
    links.forEach((link) => { link.hidden = Boolean(input) && !normalize(link.dataset.leslisSearchIdea).includes(input); });
    group.hidden = !links.some((link) => !link.hidden);
  }

  function cancel(owner) {
    owner.leslisRequest = (owner.leslisRequest || 0) + 1;
    owner.abortController?.abort();
    owner.abortController = new AbortController();
    owner.removeAttribute('loading');
    owner.input?.setAttribute('aria-busy', 'false');
  }

  function announce(owner) {
    owner.dispatchEvent(new CustomEvent('leslis:search-rendered', { bubbles: true }));
  }

  function attach(root = document) {
    const forms = root.matches?.('form[role="search"]') ? [root] : Array.from(root.querySelectorAll('form[role="search"]'));
    forms.forEach((form) => {
      if (initialized.has(form) || !form.querySelector('input[name="q"]')) return;
      initialized.add(form); attachedForms += 1;
      form.dataset.leslisSearchForm = '';
      form.querySelector('input[name="q"]').maxLength = 120;
      if (!form.querySelector('input[name="type"]')) {
        const type = document.createElement('input'); type.type = 'hidden'; type.name = 'type'; type.value = 'product,article,page'; form.append(type);
      }
      const template = document.getElementById('LeslisSearchUi');
      if (!form.querySelector('[data-leslis-search-help]') && template) form.append(template.content.cloneNode(true));
      form.addEventListener('input', () => {
        const owner = form.closest('predictive-search');
        if (owner) { cancel(owner); owner.close(); errorState(owner, false); }
        filterIdeas(form);
      });
      form.addEventListener('reset', () => {
        const owner = form.closest('predictive-search');
        if (owner) cancel(owner);
        errorState(form, false); queueMicrotask(() => filterIdeas(form));
      });
      form.addEventListener('focusout', () => {
        const owner = form.closest('predictive-search');
        queueMicrotask(() => { if (owner && !owner.leslisResultPointer && !owner.contains(document.activeElement)) { cancel(owner); owner.close(); } });
      });
      form.addEventListener('pointerdown', (event) => {
        const owner = form.closest('predictive-search');
        const result = event.target.closest('[data-predictive-search] a[href], [data-predictive-search] button');
        if (!owner || !result || event.button !== 0) return;
        owner.leslisResultPointerCleanup?.();
        const pointer = { id: event.pointerId };
        owner.leslisResultPointer = pointer;
        let timer;
        const finish = (endEvent) => {
          if (endEvent && endEvent.pointerId !== pointer.id) return;
          document.removeEventListener('pointerup', finish, true);
          document.removeEventListener('pointercancel', finish, true);
          clearTimeout(timer);
          if (owner.leslisResultPointerCleanup === finish) owner.leslisResultPointerCleanup = null;
          // Keep the result mounted through the release and its native click.
          setTimeout(() => { if (owner.leslisResultPointer === pointer) owner.leslisResultPointer = null; }, 0);
        };
        owner.leslisResultPointerCleanup = finish;
        document.addEventListener('pointerup', finish, true);
        document.addEventListener('pointercancel', finish, true);
        timer = setTimeout(finish, 20000);
      });
      form.addEventListener('click', (event) => {
        const link = event.target.closest('[data-leslis-search-idea]');
        const owner = form.closest('predictive-search');
        if (!link || !owner || !ready || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        owner.input.value = link.dataset.leslisSearchIdea;
        owner.input.dispatchEvent(new Event('input', { bubbles: true }));
        owner.input.focus();
      });
      form.addEventListener('keydown', (event) => {
        const owner = form.closest('predictive-search');
        if (event.key !== 'Escape' || event.isComposing || !owner?.hasAttribute('open')) return;
        form.leslisConsumeEscape = true;
        event.preventDefault(); event.stopPropagation(); cancel(owner); owner.close();
      });
      form.addEventListener('keyup', (event) => {
        if (event.key === 'Escape' && form.leslisConsumeEscape) { event.stopPropagation(); form.leslisConsumeEscape = false; }
      });
      filterIdeas(form);
    });
    const grid = document.getElementById('ProductGridContainer');
    if (grid && !grids.has(grid)) {
      grids.add(grid);
      new MutationObserver(() => grid.dispatchEvent(new CustomEvent('leslis:search-results-rendered', { bubbles: true }))).observe(grid, { childList: true });
    }
  }

  customElements.whenDefined('predictive-search').then(() => {
    const prototype = customElements.get('predictive-search').prototype;
    const open = prototype.open;
    prototype.open = function () { open.call(this); announce(this); };
    prototype.updateSearchForTerm = function () {};
    prototype.getSearchResults = async function (term) {
      const searchTerm = query(term);
      if (!searchTerm) { cancel(this); this.close(); return; }
      cancel(this);
      const request = this.leslisRequest;
      const controller = this.abortController;
      this.setLiveRegionLoadingState();
      this.input.setAttribute('aria-busy', 'true');
      errorState(this, false);
      const deferred = this.dispatchSearchUpdateEvent?.(searchTerm);
      deferred?.promise?.catch(() => {});
      const timer = setTimeout(() => controller.abort(), 10000);
      try {
        const url = new URL(config.predictiveUrl, location.origin);
        if (url.origin !== location.origin) throw new Error('Invalid search route');
        url.searchParams.set('q', searchTerm);
        const types = searchTypes(this.querySelector('form'));
        if (types.includes('product')) types.splice(types.indexOf('product') + 1, 0, 'collection');
        url.searchParams.set('resources[type]', types.join(','));
        url.searchParams.set('resources[limit]', String(Math.min(10, Math.max(1, Number(config.limit) || 6))));
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        url.searchParams.set('section_id', 'predictive-search');
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
        this.leslisCache ||= new Map();
        let markup = this.leslisCache.get(url.href);
        if (markup === undefined) {
          const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Search unavailable');
          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          const section = parsed.querySelector('#shopify-section-predictive-search');
          if (!section?.querySelector('[data-leslis-predictive-results]')) throw new Error('Missing search results');
          markup = section.innerHTML;
        }
        if (request !== this.leslisRequest || query(this.input.value) !== searchTerm || !this.isConnected) { deferred?.resolve({ totalCount: 0 }); return; }
        if (this.leslisCache.size >= 30) this.leslisCache.delete(this.leslisCache.keys().next().value);
        this.leslisCache.set(url.href, markup);
        this.renderSearchResults(markup);
        deferred?.resolve({ totalCount: this.getTotalResultCount() });
      } catch (error) {
        deferred?.reject(error);
        if (request === this.leslisRequest && this.isConnected) { this.close(); errorState(this, true); }
      } finally {
        clearTimeout(timer);
        if (request === this.leslisRequest) { this.removeAttribute('loading'); this.input.setAttribute('aria-busy', 'false'); }
      }
    };
    ready = true; attach();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => attach(), { once: true });
  else attach();
  document.addEventListener('shopify:section:load', (event) => attach(event.target));
  window.LeslisSearchAdapter = Object.freeze({ status: () => ({ ready, attachedForms }) });
})();
